import { ReactElement } from 'react';
import { scaleLinear, scaleTime } from '@visx/scale';
import type { ScaleLinear, ScaleTime } from 'd3-scale';
import { Key } from '@/components/TimeSeries';
import { XAxis } from './XAxis';
import { YAxis } from './YAxis';
import type { Entities, SeriesFilter } from '@/components/TimeSeries';

import { useTheme } from '@simplywallst/ui-core';

interface ChildrenProps<T extends string | number> {
  keys: number[];
  entities: Entities<T>;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  minimumX: number;
  maximumX: number;
  minimumY: number;
  maximumY: number;
  contentWidth: number;
  contentHeight: number;
}

type KeyLabelSeries = {
  key: string;
  label: string;
};
interface Props<T extends string | number> {
  series: KeyLabelSeries[];
  keys: number[];
  filter: SeriesFilter<T>;
  disabled?: Partial<SeriesFilter<T>>;
  onLegendClick?: (series: string) => void;
  entities: Entities<T>;
  minimumX: number;
  maximumX: number;
  minimumY: number;
  maximumY: number;
  formatYAxis?: (value: number) => string;
  seriesColorSelect?: (series: string) => string;
  children?: (
    props: ChildrenProps<T>
  ) => ReactElement<ChildrenProps<T>>[] | ReactElement<ChildrenProps<T>>;
  height: number;
  width: number;
  noOfColumns?: number;
}

const paddingLeft = 32;
const paddingRight = 8;
const paddingTop = 24;
const paddingBottom = 32;

const getSafeY = (n: number) => {
  if (Math.abs(n) !== Infinity && isNaN(n) === false) {
    return n;
  }
  return 0;
};

export function TimeSeries<T extends string | number>({
  children,
  disabled,
  entities,
  filter,
  formatYAxis,
  keys,
  maximumX,
  maximumY,
  minimumX,
  minimumY,
  onLegendClick,
  series,
  seriesColorSelect,
  height,
  width,
  noOfColumns,
}: Props<T>): ReactElement<Props<T>> {
  const theme = useTheme();

  const yScale: ScaleLinear<number, number> = scaleLinear({
    range: [height, paddingTop],
    domain: [minimumY, maximumY],
  });

  const contentWidth = width - paddingLeft - paddingRight;
  const columnWidth = noOfColumns ? contentWidth / noOfColumns : 0;

  const xScale: ScaleTime<number, number> = scaleTime({
    range: [paddingLeft, width - paddingRight - columnWidth],
    domain: [minimumX, maximumX],
  });

  const safeMinimumY = getSafeY(yScale.domain()[0]);
  const safeMaximumY = getSafeY(yScale.domain()[1]);

  return (
    <>
      <svg
        width="100%"
        height={height + paddingBottom}
        shapeRendering="crispEdges"
      >
        <XAxis
          xScale={xScale}
          height={height}
          rotateLabels={(width || theme.smMinRaw) < theme.smMinRaw}
          maximumX={maximumX}
          minimumX={minimumX}
        />
        <YAxis
          yScale={yScale}
          height={height}
          maximumY={safeMaximumY}
          minimumY={safeMinimumY}
          formatYAxis={formatYAxis}
          x={paddingRight}
        />
        {keys.length &&
          children?.({
            keys,
            entities,
            xScale,
            yScale,
            minimumX,
            maximumX,
            minimumY: safeMinimumY,
            maximumY: safeMaximumY,
            contentWidth,
            contentHeight: height,
          })}
      </svg>
      <div className="mt-1 ml-2 md:ml-0">
        {series.map((n: KeyLabelSeries) => {
          const { key, label } = n;
          const disable = disabled !== undefined ? disabled[key] : true;
          return (
            <Key
              key={`timeseries-key-${key}`}
              disabled={disable}
              color={
                seriesColorSelect !== undefined
                  ? seriesColorSelect(key)
                  : 'white'
              }
              active={filter[key] && !disable}
              onClick={() => {
                onLegendClick && onLegendClick(key);
              }}
            >
              {label}
            </Key>
          );
        })}
      </div>
    </>
  );
}
